"use client";

/**
 * VideoBackdrop — a self-contained, full-bleed animated backdrop that
 * mirrors the landing-page hero's "video" feel (drifting glyphs, slow orbital
 * rings, soft glow) with NO text. Drop it behind a bold wordmark or any
 * section that wants motion without copy.
 *
 * Theme-aware: reads the app's own_CSS_tokens, so it matches light/dark
 * and the active accent. Honors prefers-reduced-motion.
 */

import { useEffect } from "react";
import {
  BookOpen,
  BrainCircuit,
  GraduationCap,
  Compass,
  Sparkles,
  Network,
} from "lucide-react";

type Glyph = { Icon: typeof BookOpen; kind: string };

const POOL: Glyph[] = [
  { Icon: BookOpen, kind: "book" },
  { Icon: BrainCircuit, kind: "brain" },
  { Icon: GraduationCap, kind: "grad" },
  { Icon: Compass, kind: "compass" },
  { Icon: Sparkles, kind: "spark" },
  { Icon: Network, kind: "net" },
];

// Deterministic layout (no hydration mismatch).
const SEEDS = [11, 29, 47, 63, 77, 88, 18, 34, 52, 66, 83, 95, 5, 23, 41, 58];

type Floater = {
  Icon: typeof BookOpen;
  left: number;
  top: number;
  scale: number;
  dur: number;
  delay: number;
  spin: boolean;
};

function buildFloaters(): Floater[] {
  return SEEDS.map((s, k) => {
    const g = POOL[k % POOL.length];
    const dur = 16 + (SEEDS[(k * 7) % SEEDS.length] % 22);
    return {
      Icon: g.Icon,
      left: SEEDS[k % SEEDS.length],
      top: (SEEDS[(k * 3) % SEEDS.length] * 1.27) % 100,
      scale: 0.55 + ((SEEDS[(k * 5) % SEEDS.length] % 50) / 50) * 0.95,
      dur,
      delay: -(SEEDS[(k * 2) % SEEDS.length] % dur),
      spin: k % 3 === 0,
    };
  });
}

export default function VideoBackdrop({
  className = "",
}: {
  className?: string;
}) {
  const reduce =
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const floaters = buildFloaters();

  // Re-measure not needed; pure CSS animation. Keep an effect so the component
  // is clearly a client component and future per-frame work can hook here.
  useEffect(() => {}, []);

  return (
    <div
      className={`vb inset-0 overflow-hidden isolate ${className}`}
      aria-hidden
    >
      <div className="vb-sky">
        <div className="vb-sky__grad" />
        <div className="vb-sky__glow" />
      </div>

      {/* Orbital rings — the "video" depth */}
      <div className="vb-orbs" aria-hidden>
        <span className="vb-orb vb-orb--1" />
        <span className="vb-orb vb-orb--2" />
        <span className="vb-orb vb-orb--3" />
      </div>

      {/* Drifting characters */}
      {!reduce && (
        <div className="vb-floaters" aria-hidden>
          {floaters.map((f, k) => {
            const FIcon = f.Icon;
            return (
              <span
                key={k}
                className={`vb-floater ${f.spin ? "vb-floater--spin" : ""}`}
                style={
                  {
                    left: `${f.left}vw`,
                    top: `${f.top}vh`,
                    ["--fs" as string]: f.scale,
                    animationDuration: `${f.dur}s`,
                    animationDelay: `${f.delay}s`,
                  } as React.CSSProperties
                }
              >
                <FIcon size={26} strokeWidth={1.4} />
              </span>
            );
          })}
        </div>
      )}

      <style dangerouslySetInnerHTML={{ __html: CSS }} />
    </div>
  );
}

const CSS = `
.vb{inset:0;background:var(--background);isolation:isolate;}
.vb-sky{position:absolute;inset:0;overflow:hidden;}
.vb-sky__grad{position:absolute;inset:-14%;background:linear-gradient(178deg,color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 16%,var(--background)) 0%,var(--background) 58%,color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 8%,var(--background)) 100%);}
.vb-sky__glow{position:absolute;inset:0;background:radial-gradient(58% 44% at 76% 18%,color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 30%,transparent),transparent 70%),radial-gradient(46% 36% at 26% 64%,color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 16%,transparent),transparent 70%);}
.vb-orbs{position:absolute;inset:0;display:grid;place-items:center;}
.vb-orb{position:absolute;border-radius:50%;border:1.5px_solid color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 38%,transparent);background:radial-gradient(circle at 38% 32%,color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 22%,transparent),transparent 70%);animation:vb-drift 26s ease-in-out infinite;}
.vb-orb--1{width:min(62vw,640px);height:min(62vw,640px);}
.vb-orb--2{width:min(40vw,420px);height:min(40vw,420px);animation-duration:34s;animation-direction:reverse;opacity:.8;}
.vb-orb--3{width:min(22vw,240px);height:min(22vw,240px);animation-duration:20s;opacity:.9;}
@keyframes vb-drift{0%{transform:translate(0,0) rotate(0)}33%{transform:translate(2.5vw,-2vh) rotate(8deg)}66%{transform:translate(-2vw,1.5vh) rotate(-6deg)}100%{transform:translate(0,0) rotate(0)}}

.vb-floaters{position:absolute;inset:0;will-change:transform;}
.vb-floater{position:absolute;color:color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 55%,var(--muted-foreground));opacity:.5;transform:scale(var(--fs,1));animation:vb-float linear infinite;filter:drop-shadow(0 4px 18px color-mix(in srgb,var(--sw-accent,var(--y,#FFD600)) 30%,transparent));}
.vb-floater--spin{animation-name:vb-float,vb-spin;animation-timing-function:linear,linear;animation-iteration-count:infinite,infinite;}
@keyframes vb-float{0%{opacity:0;transform:scale(var(--fs,1)) translate(0,14vh) rotate(0)}12%{opacity:.55}88%{opacity:.45}100%{opacity:0;transform:scale(var(--fs,1)) translate(5vw,-24vh) rotate(200deg)}}
@keyframes vb-spin{to{transform:translate(0,0) rotate(360deg)}}
@media (prefers-reduced-motion:reduce){.vb-orb,.vb-floater{animation:none!important;}}
`;
